import { fetchApiGetV2, fetchApiPutV2 } from "../../../../api/api";
import { AllComponents } from "../../../../components/_default/interfaces/base";
import store from "../../../store";
import {
  setStructure,
} from "./structureActions";
import { StructureMap } from "./structureReducer";
import { NotificationManager } from "react-notifications";
import isEqual from "lodash/isEqual";
import { UPDATE_EDITOR_STATE } from "../editor/editorReducer";
import {
  logStructureValidation,
  StructureValidationError,
  validateAndRepairStructure,
} from "./structureValidation";
import {
  createUuidV7,
  getCanonicalComponentKey,
  getComponentByRef,
  getRelationChildKey,
  mapStructureMapToApiPutPayload,
  mapWebpageComponentsFromApi,
  normalizeStructureForClient,
  resolveComponentMapKey,
  uniqueByCanonicalKey,
  withComponentAliases,
} from "./structureIdentity";
import {
  LayoutRootKind,
  mergeRootIntoWebpageComponents,
  normalizeLayoutRootForInjection,
} from "./structureLayout";
const StructureAPI = {
  loadWebpageComponents,
  deleteComponent,
  addComponent,
  addExistingComponent,
  duplicateComponent,
  updateWebpageComponents,
  injectTemplateComponent,
};
export default StructureAPI;

type SaveValidationAction = "save" | "publish";

type UpdateWebpageComponentsOptions = {
  action?: SaveValidationAction;
  skipValidation?: boolean;
  sourceStructure?: StructureMap;
  baselineStructure?: StructureMap;
  useServerBaseline?: boolean;
};

type WebpageRef = string | number;

function resolveWebpageUuid(webpage: WebpageRef): string {
  if (typeof webpage === "string" && webpage.trim() !== "") {
    return webpage;
  }
  const pages = (store.getState().builder as any)?.pages?.list ?? [];
  const match = pages.find((page: any) => page?.id === webpage);
  const uuid = match?.uuid;
  if (typeof uuid === "string" && uuid.trim() !== "") {
    return uuid;
  }
  return String(webpage);
}

/** Numeric id + UUID for component nodes (local draft trees and injected subtrees). */
function resolveWebpageFieldsForComponents(webpageRef: WebpageRef): {
  webpage_id: number;
  webpage_uuid: string | null;
} {
  const pages = (store.getState().builder as any)?.pages?.list ?? [];
  if (typeof webpageRef === "string" && webpageRef.trim() !== "") {
    const uuid = webpageRef.trim();
    const page = pages.find((p: any) => p?.uuid === uuid);
    const id = page?.id;
    return {
      webpage_id: typeof id === "number" && Number.isFinite(id) ? id : 0,
      webpage_uuid: uuid,
    };
  }
  if (typeof webpageRef === "number" && Number.isFinite(webpageRef)) {
    const page = pages.find((p: any) => p?.id === webpageRef);
    const u = page?.uuid;
    return {
      webpage_id: webpageRef,
      webpage_uuid: typeof u === "string" && u.trim() !== "" ? u.trim() : null,
    };
  }
  return { webpage_id: 0, webpage_uuid: null };
}

function cloneStructure(structure: StructureMap): StructureMap {
  return normalizeStructureForClient(
    JSON.parse(
      JSON.stringify(structure ?? { webpageComponents: [], components: {} })
    )
  );
}

function isStructureMap(value: unknown): value is StructureMap {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as StructureMap;
  return (
    Array.isArray(candidate.webpageComponents) &&
    !!candidate.components &&
    typeof candidate.components === "object"
  );
}

function getCurrentStructureSnapshot(): StructureMap {
  const structure = store.getState().builder.structure as StructureMap;
  return normalizeStructureForClient({
    webpageComponents: structure?.webpageComponents ?? [],
    components: structure?.components ?? {},
  });
}


function sanitizeComponentForPayload(
  component: AllComponents,
  allComponentsById: StructureMap["components"]
): AllComponents {
  const componentKey =
    getCanonicalComponentKey(component) ?? String(component.id ?? "");
  const clonedComponent = cloneStructure({
    webpageComponents: [],
    components: { [componentKey]: component },
  }).components[componentKey] as AllComponents;

  if (
    Array.isArray(clonedComponent.childWebpageComponentRelations) &&
    clonedComponent.childWebpageComponentRelations.length > 0
  ) {
    clonedComponent.childWebpageComponentRelations =
      clonedComponent.childWebpageComponentRelations.filter((relation) => {
        if (relation.child_id === -11) {
          return false;
        }
        const childKey = getRelationChildKey(allComponentsById, relation);
        return childKey != null && !!allComponentsById[childKey];
      });
  }

  return clonedComponent;
}

function buildPutPayload(
  repairedStructure: StructureMap
): ReturnType<typeof mapStructureMapToApiPutPayload> {
  const components: StructureMap["components"] = {};

  for (const [id, component] of Object.entries(
    uniqueByCanonicalKey(repairedStructure.components ?? {})
  )) {
    if (component?.id === -11 || component?.type === "editor") {
      continue;
    }

    const sanitizedComponent = sanitizeComponentForPayload(
      component,
      repairedStructure.components
    );
    components[id] = sanitizedComponent;
  }

  return mapStructureMapToApiPutPayload({
    components,
    webpageComponents: [...(repairedStructure.webpageComponents ?? [])],
  });
}

async function getBaselineStructure(
  webpage: WebpageRef,
  baselineOverride?: StructureMap
): Promise<StructureMap> {
  if (baselineOverride) {
    return cloneStructure(baselineOverride);
  }

  // Always use fresh server structure as baseline for safety.
  const webpageUuid = resolveWebpageUuid(webpage);
  const serverStructure = await fetchApiGetV2<StructureMap>(
    `/webpages/${webpageUuid}/components`,
    {}
  );
  return normalizeStructureForClient(
    mapWebpageComponentsFromApi(serverStructure)
  );
}

export function extractStructureErrorMessage(error: unknown): string {
  if (error instanceof StructureValidationError) {
    return error.result.errors.map((issue) => issue.message).join(" ");
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  if (error && typeof error === "object" && "message" in error) {
    const payload = error as { message?: unknown; errors?: Record<string, unknown> };
    const details = payload.errors
      ? Object.values(payload.errors)
          .flatMap((value) => (Array.isArray(value) ? value : [value]))
          .map((value) => String(value))
          .join(" ")
      : "";
    return [String(payload.message), details].filter(Boolean).join(" ").trim();
  }
  return "An error occurred while saving components.";
}

export function loadWebpageComponents(webpage: WebpageRef) {
  const webpageUuid = resolveWebpageUuid(webpage);
  return fetchApiGetV2<StructureMap>(
    `/webpages/${webpageUuid}/components`,
    {}
  ).then((result) => {
    const normalized = normalizeStructureForClient(
      mapWebpageComponentsFromApi(result)
    );
    setStructure(normalized);
    return normalized;
  });
}

function getNextLocalComponentId(components: StructureMap["components"]): number {
  let minId = 0;
  for (const component of Object.values(uniqueByCanonicalKey(components))) {
    if (typeof component.id === "number" && Number.isFinite(component.id)) {
      minId = Math.min(minId, component.id);
    }
  }
  return minId <= 0 ? minId - 1 : -1;
}

function normalizeAttributes(attributes: any): AllComponents["attributes"] {
  if (!Array.isArray(attributes)) {
    const existing = attributes && typeof attributes === "object" ? attributes : {};
    return {
      ...existing,
      styles:
        existing.styles && typeof existing.styles === "object" ? existing.styles : {},
    };
  }
  const out: Record<string, any> = { styles: {} };
  for (const item of attributes) {
    if (!item || typeof item !== "object") continue;
    const key = String(item.name ?? "").trim();
    if (!key) continue;
    if (item.type === "styles") {
      if (!out.styles || typeof out.styles !== "object") {
        out.styles = {};
      }
      out.styles[key] = item.value;
      continue;
    }
    if (key === "styles") {
      out.styles =
        item.value && typeof item.value === "object" ? item.value : {};
      continue;
    }
    out[key] = item.value;
  }
  if (!out.styles || typeof out.styles !== "object") {
    out.styles = {};
  }
  return out as AllComponents["attributes"];
}

function withEditorHasEdited() {
  store.dispatch(UPDATE_EDITOR_STATE({ hasEdited: true }));
}

function applyLocalStructure(structure: StructureMap): StructureMap {
  const normalized = normalizeStructureForClient(structure);
  setStructure(normalized);
  withEditorHasEdited();
  return normalized;
}

function createComponentTreeFromDraft(
  draft: any,
  webpageRef: WebpageRef,
  parent: AllComponents | null,
  nextIdRef: { value: number }
): AllComponents {
  const uuid = createUuidV7();
  const id = nextIdRef.value;
  nextIdRef.value -= 1;
  const parentId = parent?.id ?? null;
  const { webpage_id, webpage_uuid } = resolveWebpageFieldsForComponents(webpageRef);
  const draftType = String(draft?.type ?? "");
  const isSharedHeaderFooterRoot =
    parent === null && (draftType === "header" || draftType === "footer");

  const draftLanguageCode =
    typeof draft?.languageCode === "string" && draft.languageCode.trim() !== ""
      ? draft.languageCode.trim()
      : undefined;

  const component: AllComponents = {
    id,
    uuid,
    parent_id: parentId,
    parent_uuid: parent?.uuid?.trim() ?? null,
    sort_order: Number(draft?.sort_order ?? 1),
    type: String(draft?.type ?? "section") as AllComponents["type"],
    version: Number(draft?.version ?? 1),
    webpage_id,
    webpage_uuid: webpage_uuid ?? null,
    attributes: normalizeAttributes(draft?.attributes),
    childElements: [],
    childWebpageComponentRelations: [],
    ...(isSharedHeaderFooterRoot ? { isShared: true } : {}),
    ...(draftLanguageCode != null ? { languageCode: draftLanguageCode } : {}),
  } as AllComponents;

  const children = Array.isArray(draft?.childElements) ? draft.childElements : [];
  component.childElements = children.map((child: any, index: number) =>
    createComponentTreeFromDraft(
      { ...child, sort_order: Number(child?.sort_order ?? index + 1) },
      webpageRef,
      component,
      nextIdRef
    )
  );
  component.childWebpageComponentRelations = component.childElements.map(
    (child, index) => ({
      id: 0,
      parent_id: component.id,
      parent_uuid: component.uuid,
      child_id: child.id,
      child_uuid: child.uuid,
      sort: index + 1,
      created_at: null,
      updated_at: null,
    })
  );
  return component;
}

function reparentSubtree(
  root: AllComponents,
  parent: AllComponents | null
): void {
  const parentUuid = parent?.uuid?.trim() ?? null;
  root.parent_id = parent?.id ?? null;
  (root as { parent_uuid?: string | null }).parent_uuid = parentUuid;
  const children = Array.isArray(root.childElements) ? root.childElements : [];
  root.childWebpageComponentRelations = children.map((child, index) => {
    reparentSubtree(child as AllComponents, root);
    return {
      id: 0,
      parent_id: root.id ?? null,
      parent_uuid: root.uuid?.trim() ?? null,
      child_id: child.id ?? null,
      child_uuid: child.uuid?.trim() ?? null,
      sort: index + 1,
      created_at: null,
      updated_at: null,
    };
  }) as any;
}

function flattenTree(root: AllComponents): StructureMap["components"] {
  const out: StructureMap["components"] = {};
  const stack: AllComponents[] = [root];
  while (stack.length > 0) {
    const current = stack.pop()!;
    const key = getCanonicalComponentKey(current) ?? String(current.id);
    out[key] = current;
    if (current.uuid && current.uuid !== key) {
      out[current.uuid] = current;
    }
    out[String(current.id)] = current;
    for (const child of current.childElements ?? []) {
      stack.push(child);
    }
  }
  return out;
}

/** Deep-clone a component subtree for store merge; keeps uuid/id on every node. */
function deepClonePreservingIdentity(node: AllComponents): AllComponents {
  const rawChildren = Array.isArray(node.childElements) ? node.childElements : [];
  const children = rawChildren.map((c) =>
    deepClonePreservingIdentity(c as AllComponents)
  );
  const relations = children.map((child, index) => ({
    id: 0,
    parent_id: node.id ?? null,
    parent_uuid: node.uuid?.trim() ?? null,
    child_id: child.id ?? null,
    child_uuid: child.uuid?.trim() ?? null,
    sort: index + 1,
    created_at: null,
    updated_at: null,
  }));
  return {
    ...node,
    childElements: children,
    childWebpageComponentRelations: relations as any,
  };
}

function applyWebpageContextToSubtree(
  root: AllComponents,
  webpageRef: WebpageRef
): void {
  const { webpage_id, webpage_uuid } = resolveWebpageFieldsForComponents(webpageRef);
  const walk = (n: AllComponents) => {
    n.webpage_id = webpage_id;
    (n as { webpage_uuid?: string | null }).webpage_uuid = webpage_uuid;
    for (const c of n.childElements ?? []) {
      walk(c as AllComponents);
    }
  };
  walk(root);
}

function cloneSubtreeByKey(
  source: StructureMap,
  sourceKey: string,
  parent: AllComponents | null,
  nextIdRef: { value: number }
): AllComponents | null {
  const sourceComponent = getComponentByRef(source.components, sourceKey);
  if (!sourceComponent) return null;

  const clone: AllComponents = {
    ...sourceComponent,
    id: nextIdRef.value,
    uuid: createUuidV7(),
    parent_id: parent?.id ?? null,
    childElements: [],
    childWebpageComponentRelations: [],
  };
  nextIdRef.value -= 1;

  const childKeys = (sourceComponent.childWebpageComponentRelations ?? [])
    .map((relation) => getRelationChildKey(source.components, relation))
    .filter((x): x is string => {
      if (!x) return false;
      const child = source.components[x];
      // Never clone transient editor sentinel/components into duplicated trees.
      if (!child) return false;
      if (child.id === -11 || child.type === "editor") return false;
      return true;
    });
  clone.childElements = childKeys
    .map((childKey) => cloneSubtreeByKey(source, childKey, clone, nextIdRef))
    .filter((x): x is AllComponents => !!x);
  clone.childWebpageComponentRelations = clone.childElements.map((child, index) => ({
    id: 0,
    parent_id: clone.id,
    parent_uuid: clone.uuid,
    child_id: child.id,
    child_uuid: child.uuid,
    sort: index + 1,
    created_at: null,
    updated_at: null,
  }));
  return clone;
}

function deleteComponent(webpage: WebpageRef, component: AllComponents) {
  const structure = getCurrentStructureSnapshot();
  const canonical = uniqueByCanonicalKey(structure.components);
  const rootKey =
    resolveComponentMapKey(canonical, component.uuid ?? null) ??
    resolveComponentMapKey(canonical, component.id);
  if (!rootKey) {
    return Promise.resolve(structure);
  }

  const keysToDelete = new Set<string>();
  const queue = [rootKey];
  while (queue.length > 0) {
    const key = queue.shift()!;
    if (keysToDelete.has(key)) continue;
    keysToDelete.add(key);
    const current = canonical[key];
    if (!current) continue;
    for (const relation of current.childWebpageComponentRelations ?? []) {
      const childKey = getRelationChildKey(canonical, relation);
      if (childKey && canonical[childKey]) queue.push(childKey);
    }
  }

  for (const key of keysToDelete) {
    delete canonical[key];
  }

  for (const [key, parentComponent] of Object.entries(canonical)) {
    const filtered = (parentComponent.childWebpageComponentRelations ?? []).filter(
      (relation) => {
        const childKey = getRelationChildKey(canonical, relation);
        return !childKey || !keysToDelete.has(childKey);
      }
    );
    canonical[key] = {
      ...parentComponent,
      childWebpageComponentRelations: filtered.map((relation, index) => ({
        ...relation,
        sort: index + 1,
      })),
    };
  }

  const nextRoots = structure.webpageComponents.filter((root) => !keysToDelete.has(root));
  return Promise.resolve(
    applyLocalStructure({
      components: canonical,
      webpageComponents: nextRoots,
    })
  );
}

function addComponent(webpage: WebpageRef, data: any) {
  const structure = getCurrentStructureSnapshot();
  const canonical = uniqueByCanonicalKey(structure.components);
  const nextIdRef = { value: getNextLocalComponentId(canonical) };
  const shouldUseEditorSelectionParent =
    Object.prototype.hasOwnProperty.call(data ?? {}, "parent_id") &&
    data?.parent_id == null &&
    (typeof data?.parent_uuid !== "string" || data.parent_uuid.trim() === "");
  const editorSelection = shouldUseEditorSelectionParent
    ? (store.getState().builder.editor as any)
    : null;
  const implicitParentRef =
    editorSelection?.component_uuid ??
    editorSelection?.component_id ??
    null;
  const implicitParent = getComponentByRef(canonical, implicitParentRef);
  const containerTypes = ["section", "nav", "navigation-container", "header", "footer", "Accordion"];
  const implicitParentIsContainer =
    implicitParent != null &&
    containerTypes.includes(implicitParent.type);
  const parent =
    getComponentByRef(canonical, data?.parent_uuid) ??
    getComponentByRef(canonical, data?.parent_id) ??
    (implicitParentIsContainer ? implicitParent : null);

  const root = createComponentTreeFromDraft(data, webpage, parent ?? null, nextIdRef);
  const created = flattenTree(root);
  const merged = {
    ...canonical,
    ...created,
  };

  const rootKey = getCanonicalComponentKey(root) ?? String(root.id);
  if (parent) {
    const parentKey = getCanonicalComponentKey(parent) ?? String(parent.id);
    const parentComponent = merged[parentKey];
    if (parentComponent) {
      const requestedSort = Number(data?.sort_order);
      const nextRelation = {
        id: 0,
        parent_id: parentComponent.id,
        parent_uuid: parentComponent.uuid,
        child_id: root.id,
        child_uuid: root.uuid,
        sort: Number.isFinite(requestedSort) && requestedSort > 0
          ? requestedSort
          : (parentComponent.childWebpageComponentRelations?.length ?? 0) + 1,
        created_at: null,
        updated_at: null,
      };
      const updatedRelations = [
        ...(parentComponent.childWebpageComponentRelations ?? []),
      ];
      const insertIndex =
        Number.isFinite(requestedSort) && requestedSort > 0
          ? Math.min(
              Math.max(Math.floor(requestedSort) - 1, 0),
              updatedRelations.length
            )
          : updatedRelations.length;
      updatedRelations.splice(insertIndex, 0, nextRelation);
      const resequencedRelations = updatedRelations.map((relation, index) => ({
        ...relation,
        sort: index + 1,
      }));
      merged[parentKey] = {
        ...parentComponent,
        childWebpageComponentRelations: resequencedRelations,
      };
    }
  }

  const nextRoots = parent
    ? [...structure.webpageComponents]
    : mergeRootIntoWebpageComponents(
        structure.webpageComponents,
        rootKey,
        root.type,
        data?.sort_order,
        merged
      );

  applyLocalStructure({
    components: merged,
    webpageComponents: nextRoots,
  });
  return Promise.resolve({ payload: withComponentAliases(created), addedRoot: root } as any);
}

function addExistingComponent(
  webpage: WebpageRef,
  data: {
    id: number;
    parent_id: number | null;
    sort_order: number;
    attributes?: any;
    childElements?: AllComponents[];
  }
) {
  const structure = getCurrentStructureSnapshot();
  const canonical = uniqueByCanonicalKey(structure.components);
  const source =
    getComponentByRef(canonical, data?.id) ??
    getComponentByRef(canonical, (data as any)?.uuid ?? null);
  if (!source) {
    return Promise.resolve({ payload: structure } as any);
  }

  const nextIdRef = { value: getNextLocalComponentId(canonical) };
  const parent =
    getComponentByRef(canonical, (data as any)?.parent_uuid ?? null) ??
    getComponentByRef(canonical, data.parent_id);
  const cloned = cloneSubtreeByKey(
    { components: canonical, webpageComponents: structure.webpageComponents },
    getCanonicalComponentKey(source) ?? String(source.id),
    parent ?? null,
    nextIdRef
  );
  if (!cloned) {
    return Promise.resolve({ payload: structure } as any);
  }

  const created = flattenTree(cloned);
  const merged = { ...canonical, ...created };
  const clonedKey = getCanonicalComponentKey(cloned) ?? String(cloned.id);
  if (parent) {
    const parentKey = getCanonicalComponentKey(parent) ?? String(parent.id);
    const parentComponent = merged[parentKey];
    if (parentComponent) {
      const relations = [
        ...(parentComponent.childWebpageComponentRelations ?? []),
        {
          id: 0,
          parent_id: parentComponent.id,
          parent_uuid: parentComponent.uuid,
          child_id: cloned.id,
          child_uuid: cloned.uuid,
          sort: Number(data.sort_order) || (parentComponent.childWebpageComponentRelations?.length ?? 0) + 1,
          created_at: null,
          updated_at: null,
        },
      ].map((relation, index) => ({ ...relation, sort: index + 1 }));
      merged[parentKey] = {
        ...parentComponent,
        childWebpageComponentRelations: relations,
      };
    }
  }

  const nextRoots = parent
    ? [...structure.webpageComponents]
    : Array.from(new Set([...structure.webpageComponents, clonedKey]));

  const nextStructure = applyLocalStructure({
    components: merged,
    webpageComponents: nextRoots,
  });
  return Promise.resolve({ payload: nextStructure } as any);
}

function duplicateComponent(webpage: WebpageRef, component: AllComponents) {
  const structure = getCurrentStructureSnapshot();
  const canonical = uniqueByCanonicalKey(structure.components);
  const sourceKey =
    resolveComponentMapKey(canonical, component.uuid ?? null) ??
    resolveComponentMapKey(canonical, component.id);
  if (!sourceKey) {
    return Promise.resolve({ payload: {} } as any);
  }

  const parent =
    getComponentByRef(canonical, (component as any).parent_uuid ?? null) ??
    getComponentByRef(canonical, component.parent_id);
  const nextIdRef = { value: getNextLocalComponentId(canonical) };
  const cloned = cloneSubtreeByKey(
    { components: canonical, webpageComponents: structure.webpageComponents },
    sourceKey,
    parent ?? null,
    nextIdRef
  );
  if (!cloned) {
    return Promise.resolve({ payload: {} } as any);
  }

  const created = flattenTree(cloned);
  const merged = { ...canonical, ...created };
  const clonedKey = getCanonicalComponentKey(cloned) ?? String(cloned.id);

  let nextWebpageComponents = structure.webpageComponents;

  if (parent) {
    const parentKey = getCanonicalComponentKey(parent) ?? String(parent.id);
    const parentComponent = merged[parentKey];
    if (parentComponent) {
      const existingRelations = [
        ...(parentComponent.childWebpageComponentRelations ?? []),
      ];
      const insertIndex = existingRelations.findIndex((rel) => {
        const ck = getRelationChildKey(merged, rel);
        return ck === sourceKey;
      });

      const newRelation = {
        id: 0,
        parent_id: parentComponent.id,
        parent_uuid: parentComponent.uuid,
        child_id: cloned.id,
        child_uuid: cloned.uuid,
        sort: 0,
        created_at: null,
        updated_at: null,
      };

      const relations =
        insertIndex >= 0
          ? [
              ...existingRelations.slice(0, insertIndex + 1),
              newRelation,
              ...existingRelations.slice(insertIndex + 1),
            ]
          : [...existingRelations, newRelation];

      merged[parentKey] = {
        ...parentComponent,
        childWebpageComponentRelations: relations.map((relation, index) => ({
          ...relation,
          sort: index + 1,
        })),
      };
    }
  } else {
    const roots = [...structure.webpageComponents];
    const sourceIdx = roots.indexOf(sourceKey);
    nextWebpageComponents =
      sourceIdx >= 0
        ? [
            ...roots.slice(0, sourceIdx + 1),
            clonedKey,
            ...roots.slice(sourceIdx + 1),
          ]
        : Array.from(new Set([...roots, clonedKey]));
  }

  applyLocalStructure({
    components: merged,
    webpageComponents: nextWebpageComponents,
  });
  return Promise.resolve({
    payload: withComponentAliases(created),
    duplicatedRoot: cloned,
  } as any);
}

function normalizeEmbeddedTemplateSource(
  source: any,
  fallbackComponents?: StructureMap["components"]
): any {
  if (!source || typeof source !== "object") return source;

  const byUuid = new Map<string, any>();
  const byId = new Map<string, any>();

  const toChildren = (node: any): any[] => {
    if (Array.isArray(node?.childElements)) {
      return node.childElements;
    }
    if (Array.isArray(node?.child_elements)) {
      return node.child_elements;
    }
    return [];
  };

  const indexNode = (node: any, visited: Set<any>) => {
    if (!node || typeof node !== "object" || visited.has(node)) {
      return;
    }
    visited.add(node);
    const nodeUuid = typeof node.uuid === "string" ? node.uuid.trim() : "";
    if (nodeUuid) {
      byUuid.set(nodeUuid, node);
    }
    if (node.id != null) {
      byId.set(String(node.id), node);
    }
    for (const child of toChildren(node)) {
      indexNode(child, visited);
    }
    if (node.components && typeof node.components === "object") {
      Object.values(node.components).forEach((child) => indexNode(child, visited));
    }
  };

  indexNode(source, new Set<any>());
  Object.values(fallbackComponents ?? {}).forEach((node) => {
    const nodeUuid = node?.uuid?.trim();
    if (nodeUuid && !byUuid.has(nodeUuid)) {
      byUuid.set(nodeUuid, node);
    }
    if (node?.id != null && !byId.has(String(node.id))) {
      byId.set(String(node.id), node);
    }
  });

  const visitedUuids = new Set<string>();
  const hydrate = (node: any): any => {
    if (!node || typeof node !== "object") return node;

    const nodeUuid = typeof node.uuid === "string" ? node.uuid.trim() : "";
    if (nodeUuid && visitedUuids.has(nodeUuid)) {
      return { ...node, childElements: [] };
    }
    if (nodeUuid) {
      visitedUuids.add(nodeUuid);
    }

    const directChildren = toChildren(node);
    const relations = Array.isArray(node.childWebpageComponentRelations)
      ? node.childWebpageComponentRelations
      : [];
    const relationChildren =
      directChildren.length > 0
        ? []
        : relations
            .map((relation: any) => {
              const childUuid =
                typeof relation?.child_uuid === "string"
                  ? relation.child_uuid.trim()
                  : "";
              if (childUuid) {
                return byUuid.get(childUuid) ?? null;
              }
              if (relation?.child_id != null) {
                return byId.get(String(relation.child_id)) ?? null;
              }
              return null;
            })
            .filter((x): x is any => !!x);

    const childElements = (directChildren.length > 0
      ? directChildren
      : relationChildren
    ).map((child) => hydrate(child));

    const nextRelations =
      relations.length > 0
        ? relations
        : childElements.map((child, index) => ({
            id: 0,
            parent_id: node.id ?? null,
            parent_uuid: node.uuid?.trim() ?? null,
            child_id: child?.id ?? null,
            child_uuid: child?.uuid?.trim() ?? null,
            sort: index + 1,
            created_at: null,
            updated_at: null,
          }));

    return {
      ...node,
      sort_order: node.sort_order ?? 0,
      version: node.version ?? 1,
      attributes: node.attributes ?? {},
      childElements,
      childWebpageComponentRelations: nextRelations,
    };
  };

  return hydrate(source);
}

function isEditorLikeTemplateNode(node: any): boolean {
  if (!node || typeof node !== "object") {
    return false;
  }
  const nodeType =
    typeof node.type === "string" ? node.type.trim().toLowerCase() : "";
  return node.id === -11 || nodeType === "editor";
}

/**
 * Remove editor/sentinel nodes from template draft trees before injecting into page structure.
 * This prevents duplicated/glitched editor overlays when a stored template contains editor artifacts.
 */
function sanitizeTemplateDraftForInjection(source: any): any {
  if (!source || typeof source !== "object") {
    return source;
  }

  const walk = (node: any): any | null => {
    if (!node || typeof node !== "object") {
      return null;
    }
    if (isEditorLikeTemplateNode(node)) {
      return null;
    }

    const rawChildren = Array.isArray(node.childElements)
      ? node.childElements
      : Array.isArray(node.child_elements)
      ? node.child_elements
      : [];

    const childElements = rawChildren
      .map((child: any) => walk(child))
      .filter((child): child is any => !!child);

    const childWebpageComponentRelations = childElements.map(
      (child: any, index: number) => ({
        id: 0,
        parent_id: node.id ?? null,
        parent_uuid:
          typeof node.uuid === "string" && node.uuid.trim()
            ? node.uuid.trim()
            : null,
        child_id: child?.id ?? null,
        child_uuid:
          typeof child?.uuid === "string" && child.uuid.trim()
            ? child.uuid.trim()
            : null,
        sort: index + 1,
        created_at: null,
        updated_at: null,
      })
    );

    return {
      ...node,
      childElements,
      childWebpageComponentRelations,
    };
  };

  return walk(source);
}

/** Place `draggedKey` immediately after `destinationKey` in the webpage root list (same rules as root DnD "bottom"). */
function reorderWebpageRootAfterKey(
  webpageComponents: string[],
  draggedKey: string,
  destinationKey: string
): string[] {
  const next = [...webpageComponents];
  const draggedIndex = next.indexOf(draggedKey);
  const destinationIndex = next.indexOf(destinationKey);
  if (draggedIndex === -1 || destinationIndex === -1) {
    return webpageComponents;
  }
  next.splice(draggedIndex, 1);
  if (draggedIndex > destinationIndex) {
    next.splice(destinationIndex + 1, 0, draggedKey);
  } else {
    next.splice(destinationIndex, 0, draggedKey);
  }
  return next;
}

function injectTemplateComponent(
  webpage: WebpageRef,
  templateSource: any,
  options: {
    parent_id?: number | string | null;
    parent_uuid?: string | null;
    sort_order?: number;
    /** When true, merge the source tree with original uuid/id (e.g. reusable header/footer). */
    reuseSourceIdentity?: boolean;
    /** When injecting a new top-level root (no parent), place it after this root key (sibling row template). */
    insertAfterWebpageComponentRef?: string | number | null;
    /** Explicit layout intent from header/footer pickers; applied only for top-level injects. */
    layoutRootKind?: LayoutRootKind;
  } = {}
) {
  if (!templateSource || typeof templateSource !== "object") {
    const structure = getCurrentStructureSnapshot();
    return Promise.resolve({ payload: structure, injectedRoot: null } as any);
  }

  const structure = getCurrentStructureSnapshot();
  const canonical = uniqueByCanonicalKey(structure.components);
  const nextIdRef = { value: getNextLocalComponentId(canonical) };

  const parent =
    getComponentByRef(canonical, options.parent_uuid ?? null) ??
    getComponentByRef(canonical, options.parent_id ?? null);

  const draft = sanitizeTemplateDraftForInjection(
    normalizeEmbeddedTemplateSource(templateSource, canonical)
  );
  if (!draft || isEditorLikeTemplateNode(draft)) {
    const currentStructure = getCurrentStructureSnapshot();
    return Promise.resolve({
      payload: currentStructure,
      injectedRoot: null,
    } as any);
  }
  if (options.sort_order != null) {
    draft.sort_order = options.sort_order;
  }

  const reuseIdentity = !!options.reuseSourceIdentity;
  const draftRootKey = reuseIdentity
    ? getCanonicalComponentKey(draft as AllComponents)
    : null;

  let root: AllComponents;
  let created: StructureMap["components"];

  if (reuseIdentity && draftRootKey) {
    const rootClone = deepClonePreservingIdentity(draft as AllComponents);
    reparentSubtree(rootClone, parent ?? null);
    applyWebpageContextToSubtree(rootClone, webpage);
    if (
      !parent &&
      (rootClone.type === "header" || rootClone.type === "footer")
    ) {
      (rootClone as { isShared?: boolean }).isShared = true;
    }
    root = rootClone;
    created = flattenTree(root);
  } else {
    root = createComponentTreeFromDraft(
      draft,
      webpage,
      parent ?? null,
      nextIdRef
    );
    created = flattenTree(root);
  }

  if (!parent && options.layoutRootKind) {
    root = normalizeLayoutRootForInjection(root, options.layoutRootKind);
    created = flattenTree(root);
  }

  const merged = { ...canonical, ...created };

  const rootKey = getCanonicalComponentKey(root) ?? String(root.id);
  if (parent) {
    const parentKey = getCanonicalComponentKey(parent) ?? String(parent.id);
    const parentComponent = merged[parentKey];
    if (parentComponent) {
      const requestedSort = Number(options.sort_order);
      const updatedRelations = [
        ...(parentComponent.childWebpageComponentRelations ?? []),
      ];
      const nextRelation = {
        id: 0,
        parent_id: parentComponent.id,
        parent_uuid: parentComponent.uuid,
        child_id: root.id,
        child_uuid: root.uuid,
        sort:
          Number.isFinite(requestedSort) && requestedSort > 0
            ? requestedSort
            : updatedRelations.length + 1,
        created_at: null,
        updated_at: null,
      };
      const insertIndex =
        Number.isFinite(requestedSort) && requestedSort > 0
          ? Math.min(
              Math.max(Math.floor(requestedSort) - 1, 0),
              updatedRelations.length
            )
          : updatedRelations.length;
      updatedRelations.splice(insertIndex, 0, nextRelation);
      const resequencedRelations = updatedRelations.map((relation, index) => ({
        ...relation,
        sort: index + 1,
      }));

      root.parent_id = parentComponent.id ?? null;
      (root as { parent_uuid?: string | null }).parent_uuid =
        parentComponent.uuid?.trim() ?? null;
      root.sort_order = insertIndex + 1;
      merged[rootKey] = root;

      merged[parentKey] = {
        ...parentComponent,
        childWebpageComponentRelations: resequencedRelations,
      };
    }
  }

  let nextRoots = parent
    ? [...structure.webpageComponents]
    : mergeRootIntoWebpageComponents(
        structure.webpageComponents,
        rootKey,
        root.type,
        options.sort_order,
        merged
      );

  if (
    !parent &&
    options.insertAfterWebpageComponentRef != null &&
    String(options.insertAfterWebpageComponentRef).trim() !== ""
  ) {
    const afterKey =
      resolveComponentMapKey(merged, options.insertAfterWebpageComponentRef) ??
      String(options.insertAfterWebpageComponentRef).trim();
    if (
      afterKey &&
      nextRoots.includes(rootKey) &&
      nextRoots.includes(afterKey)
    ) {
      nextRoots = reorderWebpageRootAfterKey(nextRoots, rootKey, afterKey);
    }
  }

  const nextStructure = applyLocalStructure({
    components: merged,
    webpageComponents: nextRoots,
  });

  return Promise.resolve({ payload: nextStructure, injectedRoot: root } as any);
}

function updateWebpageComponents(
  webpage: WebpageRef | null,
  structure: StructureMap,
  options: UpdateWebpageComponentsOptions = {}
) {
  if (webpage === null) {
    return Promise.reject(new Error("No active webpage selected."));
  }
  const webpageUuid = resolveWebpageUuid(webpage);

  const action = options.action ?? "save";
  const localSourceStructure = options.sourceStructure
    ? cloneStructure(options.sourceStructure)
    : normalizeStructureForClient(structure);
  const baselinePromise =
    options.baselineStructure != null
      ? Promise.resolve(cloneStructure(options.baselineStructure))
      : options.useServerBaseline
      ? getBaselineStructure(webpageUuid, options.baselineStructure)
      : Promise.resolve(cloneStructure(localSourceStructure));
  return baselinePromise
    .then((baselineStructure) => {
      const sourceStructure = cloneStructure(localSourceStructure);

      let repairedStructure = sourceStructure;
      if (!options.skipValidation) {
        const validation = validateAndRepairStructure(sourceStructure);
        logStructureValidation(action, validation);
        if (validation.errors.length > 0) {
          throw new StructureValidationError(validation);
        }
        repairedStructure = validation.structure;
        if (validation.repairs.length > 0) {
          setStructure(repairedStructure);
        }
      }

      if (isEqual(baselineStructure, repairedStructure)) {
        return repairedStructure;
      }
      const payload = buildPutPayload(repairedStructure);

      return fetchApiPutV2<unknown>(`/webpages/${webpageUuid}/components`, payload).then(
        (res) => {
        NotificationManager.success("Components saved!", null, 2000);

        if (isStructureMap(res)) {
          const nextStructure = normalizeStructureForClient(
            mapWebpageComponentsFromApi(res)
          );
          setStructure(nextStructure);
          return nextStructure;
        }

        const fallbackStructure = normalizeStructureForClient(repairedStructure);
        setStructure(fallbackStructure);
        return fallbackStructure;
      }
      );
    })
    .catch((error) => Promise.reject(error));
}
